// @ts-nocheck
import IconButton from '@mui/material/IconButton';
import PassengerFieldModalActions from '../PassengerFieldModalActions/PassengerFieldModalActions';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { XGButton, XGModal, XGSelect, XGInput } from 'xg-cl';
import XGTextarea from '../../../ProseTextarea/ProseTextarea';
import { PlusCircle, Trash } from '@phosphor-icons/react';
import * as PropTypes from 'prop-types';
import './PassengerFieldModal.css';

/**
 * Render the passenger field modal
 *
 * @param {function} onCancel - Callback function for cancel action
 * @param {function} onSubmit - Callback function for submit action
 * @param {boolean} isSubmitting - Flag indicating if form is currently submitting
 * @param {boolean} isOpen - Flag indicating if modal is currently open
 * @param {boolean} fieldTypes - List of field types
 * @param {object} fieldForms - Field form data
 * @param {function} setFieldForm - Callback function for setting field form
 * @return {JSX.Element} The update actions component
 */
const PassengerFieldModal = ({
  onCancel,
  onSubmit,
  isSubmitting,
  isAddAnswerDisabled,
  isOpen,
  fieldTypes = [],
  fieldForm = null,
  setFieldForm,
  setIsAddAnswerDisabled,
  isGlobalSettings = false
}) => {
  const { t } = useTranslation(['passengers', 'common']);
  const { enablePax2ndRound } = useFlags();
  const { passengerfieldoption = [], selectedFieldType, isEditing } = fieldForm;
  const isReadOnlySystemField = Boolean(fieldForm?.issystemfield);

  const handleChange = (evt) => {
    const { name, value } = evt.target;
    let newFieldOptions = [];

    if (name === 'fieldType') {
      const foundFieldType = fieldTypes.find(
        (fieldType) => fieldType?.label === value
      );

      const { allowoptions, key } = foundFieldType;

      if (allowoptions) {
        //Boolean should only have 2 options
        if (key === 'boolean') {
          newFieldOptions =
            passengerfieldoption?.length > 2
              ? [passengerfieldoption[0], passengerfieldoption[1]]
              : passengerfieldoption;

          setIsAddAnswerDisabled(passengerfieldoption?.length >= 2);
        } else {
          setIsAddAnswerDisabled(false);
          newFieldOptions = passengerfieldoption;
        }
      } else {
        setIsAddAnswerDisabled(false);
      }

      setFieldForm((prev) => ({
        ...prev,
        selectedFieldType: foundFieldType,
        passengerfieldoption: newFieldOptions,
        error: { ...prev.error, selectedFieldType: '' }
      }));
    } else {
      setFieldForm((prev) => ({
        ...prev,
        [name]: value,
        error: { ...prev.error, [name]: '' }
      }));
    }
  };

  const handleOptionChange = (evt, index) => {
    const { name, value } = evt.target;
    const newOptions = [...passengerfieldoption];

    newOptions[index][name] = value;
    newOptions[index].error = '';

    setFieldForm((prev) => ({
      ...prev,
      passengerfieldoption: newOptions
    }));
  };

  const handleAddOption = () => {
    const newOptions = [
      ...passengerfieldoption,
      {
        title: '',
        sequence: passengerfieldoption.length + 1,
        isselected: false
      }
    ];
    const shouldAddAnswer =
      selectedFieldType?.key === 'boolean' && passengerfieldoption.length >= 2
        ? false
        : true;

    if (shouldAddAnswer) {
      setFieldForm((prev) => ({
        ...prev,
        passengerfieldoption: newOptions,
        error: { ...prev.error, fieldOptions: '' }
      }));
    }

    if (selectedFieldType?.key === 'boolean' && newOptions.length === 2) {
      setIsAddAnswerDisabled(true);
    }
  };

  const handleRemoveOption = (sequence = 0) => {
    const newOptions = [...passengerfieldoption]
      ?.filter((option) => option.sequence !== sequence)
      ?.map((option, index) => {
        return {
          ...option,
          sequence: index + 1
        };
      });

    setFieldForm((prev) => ({
      ...prev,
      passengerfieldoption: newOptions
    }));

    if (selectedFieldType?.key === 'boolean' && newOptions.length < 2) {
      setIsAddAnswerDisabled(false);
    }
  };

  const handleInternalFieldChange = (event) => {
    const { checked } = event.target;

    setFieldForm((prev) => ({
      ...prev,
      isinternal: checked,
      isrequired: false,
      isoptional: checked ? false : true
    }));
  };

  return (
    <Box data-testid='global-passenger-field-modal'>
      <XGModal
        id='passengers-fields-modal'
        allowClosing={true}
        data-testid={'passengers-fields-moda'}
        actions={
          <PassengerFieldModalActions
            onCancel={onCancel}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            isReadOnlySystemField={isReadOnlySystemField}
          />
        }
        dismissable={false}
        open={isOpen}
        title={`${isEditing ? t('editPassengerField') : t('newPassengerField')}`}
        labelledby={`${isEditing ? t('editPassengerField') : t('newPassengerField')}`}
        maxWidth='lg'
        scroll='paper' //body
        onClose={onCancel}
      >
        <Box className='passenger-fields__modal-container'>
          <Box className='w-100' style={{ display: 'flex', gap: '16px' }}>
            <Box className='w-40'>
              <XGSelect
                id='passenger-field-field-type-select'
                label={`${t('fieldType')}`}
                name='fieldType'
                size='small'
                value={selectedFieldType?.label ?? ''}
                menuItems={fieldTypes ?? []}
                onChange={handleChange}
                error={
                  Object.hasOwn(fieldForm?.error ?? {}, 'selectedFieldType') &&
                  fieldForm?.error?.selectedFieldType !== ''
                }
                disabled={isSubmitting || isReadOnlySystemField}
              />
              {Object.hasOwn(fieldForm?.error ?? {}, 'selectedFieldType') &&
                fieldForm?.error?.selectedFieldType !== '' && (
                  <span className='text-sm-regular error-text'>
                    {fieldForm?.error?.selectedFieldType}
                  </span>
                )}
            </Box>
            <Box className='w-60 passenger-field__form-group'>
              <XGInput
                id='passenger-field-title-input'
                label={`${t('fieldTitle')}`}
                name='title'
                placeholder={t('enterFieldTitle')}
                type='text'
                value={fieldForm?.title ?? ''}
                onChange={handleChange}
                inputProps={{
                  maxLength: 100
                }}
                error={
                  Object.hasOwn(fieldForm?.error ?? {}, 'title') &&
                  fieldForm?.error?.title !== ''
                }
                hint={fieldForm?.error?.title}
                disabled={isSubmitting || isReadOnlySystemField}
              />
              <span className='title-length-hint length-hint text-sm-medium'>
                {fieldForm?.title?.length}/100
              </span>
            </Box>
          </Box>

          <Box className='w-100 passenger-field__form-group'>
            <XGTextarea
              id='passenger-field-title-input-decription'
              label={t('fieldDescription')}
              placeholder={t('enterFieldDescription')}
              value={fieldForm?.description ?? ''}
              minRows={3}
              maxRows={4}
              inputProps={{
                maxLength: 500
              }}
              name='description'
              sx={{
                width: '100%',
                height: 'auto',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                overflowY: 'auto',
                overflowX: 'hidden'
              }}
              onChange={handleChange}
              disabled={isSubmitting || isReadOnlySystemField}
            />
            <span
              className='title-length-hint length-hint text-sm-medium'
              style={{ marginTop: '58px' }}
            >
              {fieldForm?.description?.length}/500
            </span>
          </Box>

          {selectedFieldType?.allowoptions &&
            passengerfieldoption &&
            passengerfieldoption.length > 0 && (
              <Box className='w-100 passenger-field__options-container'>
                {passengerfieldoption.map((option, index) => {
                  return (
                    <Box
                      className='w-100 passenger-field__option-row'
                      key={index}
                    >
                      <Box className='w-55' style={{ position: 'relative' }}>
                        <XGInput
                          id={`passenger-field-option-input-${index}`}
                          label={`${t('answer')} #${option?.sequence ?? index}`}
                          name='title'
                          placeholder={t('enterAnswerTitle')}
                          type='text'
                          value={option?.title ?? ''}
                          onChange={(evt) => handleOptionChange(evt, index)}
                          inputProps={{
                            maxLength: 100
                          }}
                          error={
                            Object.hasOwn(option ?? {}, 'error') &&
                            option?.error !== ''
                          }
                          hint={option?.error}
                          disabled={isSubmitting || isReadOnlySystemField}
                        />
                      </Box>
                      <Box style={{ marginLeft: '24px', position: 'relative' }}>
                        <IconButton
                          aria-label='Delete'
                          onClick={() => handleRemoveOption(option?.sequence)}
                          disabled={isSubmitting || isReadOnlySystemField}
                        >
                          <Trash size={24} color='#1361D8' />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

          {selectedFieldType?.allowoptions && (
            <Box
              style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
            >
              <XGButton
                className={'passenger-field__add-answer-button'}
                id='passenger-field-add-answer-button'
                data-testid='passenger-field-add-answer-button'
                text={t('addAnswer')}
                type='primary'
                size='medium'
                variant='outlined'
                onClick={handleAddOption}
                disabled={
                  isSubmitting || isAddAnswerDisabled || isReadOnlySystemField
                }
                startIcon={
                  <PlusCircle
                    className={`passenger-fields__action-icon ${isAddAnswerDisabled ? 'passenger-fields__action-icon__disabled' : ''}`}
                  />
                }
              />
              {Object.hasOwn(fieldForm?.error ?? {}, 'fieldOptions') &&
                fieldForm?.error?.fieldOptions !== '' && (
                  <span className='text-sm-regular error-text'>
                    {fieldForm?.error?.fieldOptions}
                  </span>
                )}
            </Box>
          )}

          {isGlobalSettings && enablePax2ndRound && (
            <Box className='passenger-field__internal-option'>
              <FormControlLabel
                control={
                  <Checkbox
                    id='passenger-field-is-internal-checkbox'
                    checked={fieldForm?.isinternal ?? false}
                    onChange={handleInternalFieldChange}
                    disabled={isSubmitting || isReadOnlySystemField}
                    sx={{ padding: '0px', marginRight: '8px' }}
                  />
                }
                label={t('markAsInternalField')}
                style={{ marginLeft: '0px' }}
              />
              <Typography className='passenger-field__internal-option-help'>
                {t('internalFieldHelp')}
              </Typography>
            </Box>
          )}
        </Box>
      </XGModal>
    </Box>
  );
};

PassengerFieldModal.propTypes = {
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
  isOpen: PropTypes.bool,
  isAddAnswerDisabled: PropTypes.bool,
  setIsAddAnswerDisabled: PropTypes.func,
  fieldTypes: PropTypes.array,
  fieldForm: PropTypes.object,
  setFieldForm: PropTypes.func,
  isGlobalSettings: PropTypes.bool
};

export default PassengerFieldModal;
